import { useJsApiLoader, GoogleMap, Marker, DirectionsService, DirectionsRenderer, DirectionsResult } from "@react-google-maps/api"
import { addWaypoint as addInqueryWaypoint, removeWaypoint as removeInquieryWaypoint, changeWaypointLocation, swapWaypoints, updateWaypoint } from '../../store/inquerySlice';
import { useSelector, useDispatch } from 'react-redux';
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { toast } from "react-toastify";

const CENTER = { lat: 58.55385369450994, lng: 25.566642998280088 };

const RouteMap = ({ ...props }) => {
    const dispatch = useDispatch();

    const [ directions, setDirections ] = useState(null);
    const [map, setMap] = useState(null);
    // Select the raw waypoints array from the store
    const rawWaypoints = useSelector((state) => state.inquery.waypoints);
    const [ firstZoomDone, setFirstZoomDone ] = useState(false);

    const zoomedOnWaypoint = useSelector((state) => state.inquery.zoomedOnWaypoint);

    // Memoize the filtered waypoints needed for rendering and logic
    const waypoints = useMemo(() => {
        return rawWaypoints.filter(wp => wp.location);
    }, [rawWaypoints]); // Depend on the raw array reference

    // Memoize the stable string key based on the raw array
    const waypointsKey = useMemo(() => {
        return rawWaypoints
            .filter(wp => wp.location) // Filter here as well
            .map(wp => `${wp.location.lat},${wp.location.lng}`)
            .join('|');
    }, [rawWaypoints]); // Depend on the raw array reference

    useEffect(() => {
        if(zoomedOnWaypoint && map) {
            map.panTo(zoomedOnWaypoint);
            map.setZoom(18);
            map.setMapTypeId('satellite');

            if(!firstZoomDone) {
                setFirstZoomDone(true);
                toast.info("Kasuta hiirt, et kaardil peatuskohti ümber tõsta");
            }
        }
    }, [zoomedOnWaypoint, map, firstZoomDone]);

    useEffect(() => {
        if (!window.google || !window.google.maps || !window.google.maps.DirectionsService) {
            console.log("Google Maps API not ready for DirectionsService");
            return;
        }

        // Use the memoized 'waypoints' array for checks and logic
        if(waypoints.length < 2) {
            setDirections(null);
            return;
        }

        const DirectionsServiceInstance = new window.google.maps.DirectionsService();

        // Use the memoized 'waypoints' array
        const origin = waypoints[0].location;
        const destination = waypoints[waypoints.length - 1].location;
        const intermediateWaypoints = waypoints.length > 2
            ? waypoints.slice(1, -1).map(w => ({ location: w.location, stopover: true }))
            : [];

        (async () => {
            let newRoute = null;
            try {
                newRoute = await DirectionsServiceInstance.route({
                    origin: origin,
                    destination: destination,
                    travelMode: window.google.maps.TravelMode.DRIVING,
                    avoidFerries: true,
                    waypoints: intermediateWaypoints,
                });
                setDirections(newRoute);
            } catch(e) {
                console.error("Directions request failed:", e);
                setDirections(null);
            }
        })();

    }, [waypointsKey]); // Use the stable string key as the dependency

    const onLoad = useCallback(function callback(mapInstance) {
        console.log("map initialized");
        mapInstance.setOptions({
            mapTypeControl: true,
            streetViewControl: false,
            mapTypeControlOptions: {
                position: window.google.maps.ControlPosition.TOP_RIGHT
            },
            fullscreenControl: false,
            zoomControl: true,
        });
        setMap(mapInstance);
    }, []);

    const onUnmount = useCallback(function callback(map) {
        console.log("map uninitialized");
        setMap(null);
    }, []);

    const onZoomChanged = useCallback(() => {
        if(!map) return;
        if(map.getZoom() < 16 && map.getMapTypeId() !== 'roadmap') {
            map.setMapTypeId('roadmap');
        }
    }, [map]);

    return (
        <GoogleMap
            mapContainerStyle={{ height: `100%`, flex: 1 }}
            zoom={7}
            center={CENTER}
            onLoad={onLoad}
            onUnmount={onUnmount}
            clickableIcons={false}
            onZoomChanged={onZoomChanged}
            options={{
                mapTypeControl: true,
                streetViewControl: false,
                fullscreenControl: false,
                zoomControl: true,
                mapTypeControlOptions: {
                     position: window.google.maps.ControlPosition.TOP_RIGHT
                },
            }}
            {...props}
        >
            {/* Use the memoized 'waypoints' array for rendering markers */}
            { waypoints.map(place => (
                place.location && (
                    <Marker
                        key={place.id}
                        position={place.location}
                        draggable
                        onDragEnd={(e) => dispatch(changeWaypointLocation({ id: place.id, latLng: { lat: e.latLng.lat(), lng: e.latLng.lng() } }))}
                        onDragStart={() => setDirections(null)}
                    />
                )
            )) }
            {
                directions && <DirectionsRenderer options={{ preserveViewport: true, suppressMarkers: true }} directions={directions}/>
            }
        </GoogleMap>
    );
};

export default RouteMap;